import { FormControl, FormGroup } from '@angular/forms';
import {
  AppearanceConfig,
  AppearanceMode,
  defaultAppearance,
  ModeOverrides,
  setModeEnabled,
} from '@finxone-platform/shared/sys-config-types';
import { buildElevationOverrideFormGroup } from './global-theme-elevation-form.utils';
import { buildCustomGroupFormGroup } from './global-theme-palette-custom-groups-form.utils';
import { renameFormGroupControl } from './global-theme-form-group-reorder.utils';

/*
 * `appearance` and `modeOverrides` are root-level theme controls that only exist once a
 * theme has an appearance (on load, or when a second mode is first enabled), so a theme
 * without one saves exactly as before. Mode overrides are sparse: a palette key with no
 * override has no control.
 */

export function buildAppearanceFormGroup(appearance: AppearanceConfig): FormGroup {
  return new FormGroup({
    baseMode: new FormControl(appearance.baseMode),
    modes: new FormControl(appearance.modes),
    storedPreference: new FormControl(appearance.storedPreference),
    fallbackMode: new FormControl(appearance.fallbackMode),
  });
}

export function buildModeOverridesFormGroup(modeOverrides: ModeOverrides = {}): FormGroup {
  const modes: Record<string, FormGroup> = {};
  for (const [mode, override] of Object.entries(modeOverrides)) {
    const groups: Record<string, FormGroup> = {};
    for (const [groupName, colours] of Object.entries(override?.palette ?? {})) {
      groups[groupName] = buildCustomGroupFormGroup(colours);
    }
    modes[mode] = new FormGroup<Record<string, FormGroup>>({
      // A mode holding only elevation overrides keeps its stored shape (no empty palette).
      ...((override?.palette || !override?.elevation) && { palette: new FormGroup(groups) }),
      ...(override?.elevation && { elevation: buildElevationOverrideFormGroup(override.elevation) }),
    });
  }
  return new FormGroup(modes);
}

export function getModeOverridesFormGroup(themeFormGroup: FormGroup): FormGroup | null {
  return (themeFormGroup.get('modeOverrides') as FormGroup) ?? null;
}

/** Enables or disables a mode, creating the appearance controls on first use. */
export function setAppearanceModeEnabled(
  themeFormGroup: FormGroup,
  mode: AppearanceMode,
  enabled: boolean,
): void {
  const appearanceFormGroup = themeFormGroup.get('appearance');
  const current: AppearanceConfig = appearanceFormGroup?.value ?? defaultAppearance('light');
  const modeOverrides = getModeOverridesFormGroup(themeFormGroup);
  const next = setModeEnabled(current, mode, enabled, modeOverrides?.value);
  if (next === current) return;

  if (!appearanceFormGroup) {
    themeFormGroup.addControl('modeOverrides', buildModeOverridesFormGroup(), { emitEvent: false });
    themeFormGroup.addControl('appearance', buildAppearanceFormGroup(next));
    return;
  }
  // A relabelled base mode can't keep an (empty) override entry of its own.
  if (next.baseMode !== current.baseMode) modeOverrides?.removeControl(next.baseMode, { emitEvent: false });
  appearanceFormGroup.setValue(next);
}

/** Writes one override value, creating the mode and group controls as needed. */
export function setPaletteOverride(
  themeFormGroup: FormGroup,
  mode: AppearanceMode,
  groupName: string,
  colourKey: string,
  value: string,
): void {
  const modeOverrides = getModeOverridesFormGroup(themeFormGroup);
  if (!modeOverrides || themeFormGroup.get('appearance.baseMode')?.value === mode) return;

  if (!value) {
    clearPaletteOverride(modeOverrides, mode, groupName, colourKey);
    return;
  }

  if (!modeOverrides.get(mode)) modeOverrides.addControl(mode, new FormGroup({}), { emitEvent: false });
  // An elevation override may have created the mode's entry without a palette.
  const modeGroup = modeOverrides.get(mode) as FormGroup;
  if (!modeGroup.get('palette')) modeGroup.addControl('palette', new FormGroup({}), { emitEvent: false });
  const palette = modeOverrides.get([mode, 'palette']) as FormGroup;
  if (!palette.get(groupName)) palette.addControl(groupName, new FormGroup({}), { emitEvent: false });
  const group = palette.get(groupName) as FormGroup;

  const control = group.get(colourKey);
  if (control) control.setValue(value);
  else group.addControl(colourKey, new FormControl(value));
}

/** Removes one override so the base palette colour applies again. */
function clearPaletteOverride(
  modeOverrides: FormGroup,
  mode: AppearanceMode,
  groupName: string,
  colourKey: string,
): void {
  const palette = modeOverrides.get([mode, 'palette']) as FormGroup | null;
  const group = palette?.get(groupName) as FormGroup | null;
  if (!palette || !group?.get(colourKey)) return;

  group.removeControl(colourKey);
  if (Object.keys(group.controls).length === 0) palette.removeControl(groupName);
}

/** Override palette groups across every mode, enabled or not. */
function overridePalettes(themeFormGroup: FormGroup): FormGroup[] {
  const modeOverrides = getModeOverridesFormGroup(themeFormGroup);
  if (!modeOverrides) return [];
  return Object.keys(modeOverrides.controls)
    .map((mode) => modeOverrides.get([mode, 'palette']) as FormGroup | null)
    .filter((palette): palette is FormGroup => !!palette);
}

/* Keep overrides keyed in step with renames and removals on the base palette. */

export function renameOverrideGroup(themeFormGroup: FormGroup, oldName: string, newName: string): void {
  for (const palette of overridePalettes(themeFormGroup)) {
    if (palette.get(oldName)) renameFormGroupControl(palette, oldName, newName);
  }
}

export function removeOverrideGroup(themeFormGroup: FormGroup, groupName: string): void {
  for (const palette of overridePalettes(themeFormGroup)) palette.removeControl(groupName);
}

export function renameOverrideColour(
  themeFormGroup: FormGroup,
  groupName: string,
  oldName: string,
  newName: string,
): void {
  for (const palette of overridePalettes(themeFormGroup)) {
    const group = palette.get(groupName) as FormGroup | null;
    if (group?.get(oldName)) renameFormGroupControl(group, oldName, newName);
  }
}

export function removeOverrideColour(themeFormGroup: FormGroup, groupName: string, colourKey: string): void {
  for (const palette of overridePalettes(themeFormGroup)) {
    (palette.get(groupName) as FormGroup | null)?.removeControl(colourKey);
  }
}
